import React from "react";
import { Row, Col, Typography, Divider, Tag, Space } from "antd";
import {
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { parseLocalDate } from "../../../easyapp.utils";
const { Text } = Typography;

/**
 * Format a date string using local timezone to prevent day shifts.
 * Uses parseLocalDate to extract date components before formatting with dayjs.
 */
const formatLocalDate = (dateString: string | undefined, format: string): string => {
  if (!dateString) return "N/A";
  const localDate = parseLocalDate(dateString);
  if (!localDate) return "N/A";
  return dayjs(localDate).format(format);
};

/**
 * Calculate age from a date string using local timezone.
 */
const calculateAge = (dateString: string | undefined): number => {
  if (!dateString) return 0;
  const localDate = parseLocalDate(dateString);
  if (!localDate) return 0;
  return dayjs().diff(dayjs(localDate), "year");
};

export const ApplicantCard = React.memo(
  ({ applicant, index, incomeData }: any) => {
    const monthlyIncome =
      incomeData?.applicantsIncome?.[index]?.monthlyIncome ??
      incomeData?.applicantsBankIncome?.[index]?.included_income ??
      0;

    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Space direction="vertical" size={4}>
              <Text>
                <MailOutlined style={{ marginRight: 8 }} />
                {applicant.email}
              </Text>
              <Text>
                <PhoneOutlined style={{ marginRight: 8 }} />
                {applicant.phone_number}
              </Text>
              {applicant.dob && (
                <Text type="secondary">
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  {formatLocalDate(applicant.dob, "MMM DD, YYYY")} (
                  {calculateAge(applicant.dob)} years old)
                </Text>
              )}
            </Space>
          </Col>
          <Col xs={24} md={12}>
            <Space direction="vertical" size={4}>
              <Text strong>Monthly Income</Text>
              <Text>${monthlyIncome.toLocaleString() || 0}</Text>
              <Space style={{ marginTop: 8, justifyContent: "flex-end" }} wrap>
                <Tag
                  color={
                    applicant.employment_status === "employed"
                      ? "success"
                      : "warning"
                  }
                >
                  {applicant.employment_status || "Unknown"}
                </Tag>
                {applicant.misc_smoker && <Tag color="orange">Smoker</Tag>}
              </Space>
            </Space>
          </Col>
        </Row>
        {(applicant.current_residence_address ||
          applicant.emergency_contact_name) && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Row gutter={[16, 8]}>
              {applicant.current_residence_address && (
                <Col xs={24} md={12}>
                  <Space direction="vertical" size={4}>
                    <Text strong>
                      <EnvironmentOutlined style={{ marginRight: 8 }} />
                      Current Residence
                    </Text>
                    <Text>
                      {applicant.current_residence_address}
                      {applicant.current_residence_unit &&
                        ` #${applicant.current_residence_unit}`}
                    </Text>
                    <Text>
                      {applicant.current_residence_city},{" "}
                      {applicant.current_residence_state}{" "}
                      {applicant.current_residence_zip}
                    </Text>
                    <Text type="secondary">
                      ${applicant.current_residence_rent || "N/A"}/month • Since{" "}
                      {formatLocalDate(applicant.current_residence_start_date, "MMM YYYY")}
                    </Text>
                  </Space>
                </Col>
              )}
              {applicant.emergency_contact_name && (
                <Col xs={24} md={12}>
                  <Space direction="vertical" size={4}>
                    <Text strong>
                      <PhoneOutlined style={{ marginRight: 8 }} />
                      Emergency Contact
                    </Text>
                    <Text>
                      {applicant.emergency_contact_name} (
                      {applicant.emergency_contact_relationship})
                    </Text>
                    <Text>{applicant.emergency_contact_phone}</Text>
                  </Space>
                </Col>
              )}
            </Row>
          </>
        )}
      </div>
    );
  }
);
